import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { FeatureFlagGuard } from 'app/core/auth/guards/feature-flag.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { InitialDataResolver } from 'app/app.resolvers';
import { environment } from 'environments/environment';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/example'
    { path: '', pathMatch: 'full', redirectTo: 'dashboard' },

    // Redirect signed in user to the '/example'
    //
    // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'dashboard' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () =>
                    import('app/modules/auth/confirmation-required/confirmation-required.module').then(
                        (m) => m.AuthConfirmationRequiredModule,
                    ),
            },
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import('app/modules/auth/forgot-password/forgot-password.module').then(
                        (m) => m.AuthForgotPasswordModule,
                    ),
            },
            {
                path: 'reset-password',
                loadChildren: () =>
                    import('app/modules/auth/reset-password/reset-password.module').then(
                        (m) => m.AuthResetPasswordModule,
                    ),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.module').then(
                        (m) => m.AuthSignInModule,
                    ),
            },
            {
                path: 'sign-up',
                loadChildren: () =>
                    import('app/modules/auth/sign-up/sign-up.module').then(
                        (m) => m.AuthSignUpModule,
                    ),
            },
            // {
            //     path: 'temp-maintenance-page',
            //     loadChildren: () => import('app/modules/auth/temp-maintenance-page/temp-maintenance-page.module').then(m => m.TempMaintenancePageModule)
            // },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.module').then(
                        (m) => m.AuthSignOutModule,
                    ),
            },
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import('app/modules/auth/unlock-session/unlock-session.module').then(
                        (m) => m.AuthUnlockSessionModule,
                    ),
            },
        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'home',
                loadChildren: () =>
                    import('app/modules/landing/home/home.module').then(
                        (m) => m.LandingHomeModule,
                    ),
            },
        ],
    },

    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'dashboard',
                loadChildren: () =>
                    import('app/components/dashboard/dashboard.module').then(
                        (m) => m.DashboardModule,
                    ),
            },
            {
                path: 'work-order',
                loadChildren: () =>
                    import('app/components/work-order/work-order.module').then(
                        (m) => m.WorkOrderModule,
                    ),
            },
            {
                path: 'tracking-module',
                loadChildren: () =>
                    import('app/components/tracking-module/tracking-module.module').then(
                        (m) => m.TrackingModule,
                    ),
            },
            {
                path: 'tracking-module-beta',
                canActivate: [FeatureFlagGuard],
                canActivateChild: [FeatureFlagGuard],
                data: { featureFlag: 'trackingModuleBeta' },
                loadChildren: () =>
                    import('app/components/tracking-module-new/tracking-module-new.module').then(
                        (m) => m.TrackingModuleNewModule,
                    ),
            },
            {
                path: 'daily-report',
                loadChildren: () =>
                    import('app/components/daily-report/daily-report.module').then(
                        (m) => m.DailyReportModule,
                    ),
            },
            {
                path: '',
                loadChildren: () =>
                    import('app/components/shift-data-analysis/shift-data-analysis.module').then(
                        (m) => m.ShiftDataAnalysisModule,
                    ),
            },
            {
                path: 'ie-anomalies',
                canActivate: [FeatureFlagGuard],
                canActivateChild: [FeatureFlagGuard],
                data: { featureFlag: 'ieAnomalies' },
                loadChildren: () =>
                    import('app/components/ie-anomalies/ie-anomalies.module').then(
                        (m) => m.IeAnomaliesModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'admin-home',
                loadChildren: () =>
                    import('app/components/home/admin-home.module').then(
                        (m) => m.AdminHomeModule,
                    ),
            },
            {
                path: 'department',
                loadChildren: () =>
                    import('app/components/department/department.module').then(
                        (m) => m.DepartmentModule,
                    ),
            },
            {
                path: 'sla',
                loadChildren: () =>
                    import('app/components/sla/sla.module').then(
                        (m) => m.SlaModule,
                    ),
            },
            {
                path: 'exclusion',
                loadChildren: () =>
                    import('app/components/exclusion/exclusion.module').then(
                        (m) => m.ExclusionModule,
                    ),
            },
            {
                path: 'reports',
                loadChildren: () =>
                    import('app/components/reports/reports.module').then(
                        (m) => m.ReportsModule,
                    ),
            },
            {
                path: 'add-new-exclusion',
                loadChildren: () =>
                    import('app/components/add-new-exclusion/add-new-exclusion.module').then(
                        (m) => m.AddNewExclusionModule,
                    ),
            },
            {
                path: 'app-manage-user',
                loadChildren: () =>
                    import('app/components/manage-user/manage-user.module').then(
                        (m) => m.ManageUserModule,
                    ),
            },
            {
                path: 'defalt-map-selection',
                loadChildren: () =>
                    import('app/components/default-map-selection/default-map-selection.module').then(
                        (m) => m.DefaultMapSelectionModule,
                    ),
            },
            {
                path: 'app-add-new-user',
                loadChildren: () =>
                    import('app/components/add-new-user/add-new-user.module').then(
                        (m) => m.AddNewUserComponentModule,
                    ),
            },
        ],
    },
];
